// EchoCodesList.js - Compact 2-column grid display with drag-and-drop

import React, { useState } from 'react';
import { getCodeDetails, CATEGORY_NAMES } from './echo-codes/echoCodesLoader';
import { sortCodesByPriority } from './echo-codes/codeOrderingConfig';
import { getCategoryCount } from './parseEchoCodes';
import './EchoCodesList.css';

/**
 * Display a single code with horizontal modifier buttons (draggable)
 */
const EchoCodeItem = ({
  code,
  modifier,
  value,
  onModifierChange,
  onValueChange,
  onRemove,
  codeDetails,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging
}) => {
  const availableModifiers = codeDetails?.supplement || [];
  const isNumeric = codeDetails?.supplementType === 'numeric';
  const isText = codeDetails?.supplementType === 'text';

  return (
    <div
      className={`echo-code-item ${isDragging ? 'dragging' : ''}`}
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="echo-code-header">
        <span className="echo-code-drag-handle">⋮⋮</span>
        <span className="echo-code-code">{code}</span>
        <button
          className="echo-code-remove"
          onClick={() => onRemove()}
          title="Supprimer"
        >
          ✕
        </button>
      </div>
      <div className="echo-code-name">{codeDetails?.nom || ''}</div>

      {/* Numeric value input */}
      {isNumeric && onValueChange && (
        <div className="echo-code-value-input">
          <input
            type="number"
            className="echo-value-field"
            placeholder="Valeur..."
            value={value !== null && value !== undefined ? value : ''}
            onChange={(e) => {
              const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
              onValueChange(newValue);
            }}
          />
        </div>
      )}

      {/* Display value if numeric but no edit callback (read-only) */}
      {isNumeric && !onValueChange && value !== null && value !== undefined && (
        <div className="echo-code-value-display">
          Valeur: {value}
        </div>
      )}

      {/* Text value input (for ranges like "35-40") */}
      {isText && onValueChange && (
        <div className="echo-code-value-input">
          <input
            type="text"
            className="echo-value-field"
            placeholder={codeDetails?.textConfig?.placeholder || "Valeur..."}
            value={value !== null && value !== undefined ? value : ''}
            onChange={(e) => {
              const newValue = e.target.value === '' ? null : e.target.value;
              onValueChange(newValue);
            }}
          />
        </div>
      )}

      {/* Display value if text but no edit callback (read-only) */}
      {isText && !onValueChange && value !== null && value !== undefined && (
        <div className="echo-code-value-display">
          Valeur: {value}
        </div>
      )}

      {availableModifiers.length > 0 && (
        <div className="echo-code-modifiers">
          {availableModifiers.map(mod => {
            // Case-insensitive comparison for modifier matching
            const isActive = modifier && mod &&
              modifier.toLowerCase() === mod.toLowerCase();

            return (
              <button
                key={mod}
                className={`echo-modifier-chip ${isActive ? 'active' : ''}`}
                onClick={() => onModifierChange(isActive ? null : mod)}
                title={mod}
              >
                {mod}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Display codes for a single category in 2-column grid with drag-and-drop
 */
const EchoCategorySection = ({
  category,
  categoryName,
  codes,
  onModifierChange,
  onValueChange,
  onRemove,
  onReorder
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  if (!codes || codes.length === 0) {
    return null; // Don't show empty categories
  }

  // Sort codes by priority order
  const sortedCodes = sortCodesByPriority(codes, category);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    // Reorder the sorted codes array
    const newCodes = [...sortedCodes];
    const [draggedItem] = newCodes.splice(draggedIndex, 1);
    newCodes.splice(dropIndex, 0, draggedItem);

    // Notify parent to update the codes
    onReorder(category, newCodes);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="echo-category-section">
      <div className="echo-category-header">
        <span className="echo-category-name">{categoryName}</span>
        <span className="echo-category-count">{sortedCodes.length}</span>
      </div>

      <div className="echo-category-grid">
        {sortedCodes.map((codeObj, index) => {
          const codeDetails = getCodeDetails(codeObj.code);
          return (
            <EchoCodeItem
              key={`${codeObj.code}-${index}`}
              code={codeObj.code}
              modifier={codeObj.modifier}
              value={codeObj.value}
              codeDetails={codeDetails}
              isDragging={draggedIndex === index}
              onModifierChange={(newModifier) =>
                onModifierChange(category, codeObj.code, newModifier)
              }
              onValueChange={onValueChange ? (newValue) =>
                onValueChange(category, codeObj.code, newValue) : null
              }
              onRemove={() => onRemove(category, codeObj.code, true)}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * Main component to display all echo codes in compact 2-column layout with drag-and-drop
 */
const EchoCodesList = ({ codes, onModifierChange, onValueChange, onRemove, onReorder }) => {
  // Check if any codes exist
  const hasAnyCodes = Object.keys(CATEGORY_NAMES).some(category =>
    getCategoryCount(codes, category) > 0
  );

  if (!hasAnyCodes) {
    return (
      <div className="echo-codes-empty">
        <p>Aucun code détecté</p>
        <p className="echo-codes-empty-hint">
          Les codes sont extraits automatiquement ou ajoutez-les via la recherche
        </p>
      </div>
    );
  }

  return (
    <div className="echo-codes-list">
      {Object.entries(CATEGORY_NAMES).map(([category, categoryName]) => (
        <EchoCategorySection
          key={category}
          category={category}
          categoryName={categoryName}
          codes={codes[category] || []}
          onModifierChange={onModifierChange}
          onValueChange={onValueChange}
          onRemove={onRemove}
          onReorder={onReorder}
        />
      ))}
    </div>
  );
};

export default EchoCodesList;
